/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { OpenAPIHousien } from '@/services/requests/core/OpenAPI';
import { request as __request } from '@/services/requests/core/request';
import { FF_MOCK_SPREADSHEET_API } from '@/shared/utils/feature-flag';
import type {
  ExportFormat,
  ExportResult,
  ImportCommitResult,
  ImportDetail,
  ImportPreview,
  ImportSession,
  ImportSettings,
  ImportUploadTicket,
  SpreadsheetEntity,
  SpreadsheetHubData,
} from '../types';
import {
  mockCommit,
  mockCreateSession,
  mockGetPreview,
  mockGetSession,
  mockHub,
  mockImportDetail,
  mockPatchSession,
  mockUndo,
} from './spreadsheets.mock';

/**
 * The single seam between the spreadsheet screens and the backend.
 *
 * Most of it is real now: opening a session, reading the preview, patching
 * settings, committing and undoing all reach the backend. What is still
 * {@link SpreadsheetApiUnavailableError} is the work that was deliberately
 * deferred -- the file export and the report download, which wait on
 * file exports and
 * report downloads, which have no endpoint yet.
 *
 * A deferred call throws rather than answering emptily, so the screens show
 * `DataStateError` instead of pretending. `FF_MOCK_SPREADSHEET_API` swaps the
 * whole seam for fixtures; it is off by default, because leaving it on would
 * hide the endpoints that do exist.
 */

/** Thrown by a call whose endpoint is still deferred. */
export class SpreadsheetApiUnavailableError extends Error {
  constructor(endpoint: string) {
    super(`Spreadsheet endpoint not implemented yet: ${endpoint}`);
    this.name = 'SpreadsheetApiUnavailableError';
  }
}

/** Fails loudly for a call the backend cannot serve yet. */
function notYet(endpoint: string): never {
  throw new SpreadsheetApiUnavailableError(endpoint);
}

/** POST /agency/{agency_id}/spreadsheet-imports/uploads */
function openUploadTicket(agencyId: string, fileName: string): Promise<ImportUploadTicket> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/spreadsheet-imports/uploads',
    path: { agencyId },
    body: { file_name: fileName },
    mediaType: 'application/json',
  });
}

/**
 * Sends the workbook straight to storage, against the signed policy.
 *
 * Deliberately `fetch` and not the generated client: this request leaves
 * for the bucket, not for our API, and must carry neither our bearer token
 * nor our JSON content type. The signed `fields` go in before the file
 * part — S3 reads the policy from what precedes it and ignores anything
 * after.
 */
async function uploadWorkbook(ticket: ImportUploadTicket, file: File): Promise<void> {
  const form = new FormData();
  Object.entries(ticket.upload.fields).forEach(([name, value]) => {
    form.append(name, value);
  });
  form.append('file', file);
  const response = await fetch(ticket.upload.url, { method: 'POST', body: form });
  if (!response.ok) {
    // Left without a `status`, so the screen says "we couldn't send it"
    // rather than translating the bucket's own 403 into a sentence about
    // permissions the person does hold.
    throw new Error(`Workbook upload rejected with ${response.status}`);
  }
}

/**
 * Opens an import: ticket, upload, then analysis.
 *
 * The file used to travel base64 inside the JSON body. In production the
 * CloudFront WAF inspects the first 8 KB of a body, and
 * `SizeRestrictions_BODY` blocked every spreadsheet bigger than roughly
 * sixty rows — the browser saw a bare 403 and this screen called it a
 * permission problem. Going straight to the bucket also clears the 6 MB
 * ceiling on a synchronous Lambda payload, which was the next wall.
 */
export async function createImportSession(
  agencyId: string,
  input: { file: File },
): Promise<ImportSession> {
  if (FF_MOCK_SPREADSHEET_API) return mockCreateSession(input);
  const ticket = await openUploadTicket(agencyId, input.file.name);
  await uploadWorkbook(ticket, input.file);
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/spreadsheet-imports',
    path: { agencyId },
    body: {
      file_name: input.file.name,
      session_id: ticket.session_id,
    },
    mediaType: 'application/json',
  });
}

/**
 * Re-opens a stored session after a reload.
 *
 * The screen kept the session only in memory, so a reload (or Chrome
 * killing the tab) dropped the import and every answer given on it.
 *
 * @param agencyId - The agency the import belongs to.
 * @param sessionId - The session the URL carried.
 * @returns The session, or `null` when it no longer exists.
 */
export async function reopenImportSession(
  agencyId: string,
  sessionId: string,
): Promise<ImportSession | null> {
  if (FF_MOCK_SPREADSHEET_API) return mockGetSession(sessionId);
  try {
    return await __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/spreadsheet-imports/{session_id}',
      path: { agencyId, session_id: sessionId },
    });
  } catch {
    return null;
  }
}

/** GET /agencies/{agency_id}/spreadsheet-imports/{session_id}/preview */
export function getImportPreview(
  agencyId: string,
  sessionId: string,
  params: { status?: string; offset?: number; limit?: number } = {},
): Promise<ImportPreview> {
  if (FF_MOCK_SPREADSHEET_API) return mockGetPreview(sessionId, params);
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/spreadsheet-imports/{session_id}/preview',
    path: { agencyId, session_id: sessionId },
  });
}

/** PATCH /agencies/{agency_id}/spreadsheet-imports/{session_id} */
export function patchImportSession(
  agencyId: string,
  sessionId: string,
  patch: ImportSettings,
  status?: string,
): Promise<ImportPreview> {
  if (FF_MOCK_SPREADSHEET_API) return mockPatchSession(sessionId, patch, status);
  return __request(OpenAPIHousien, {
    method: 'PATCH',
    url: '/agency/{agencyId}/spreadsheet-imports/{session_id}',
    path: { agencyId, session_id: sessionId },
    body: patch,
    mediaType: 'application/json',
  });
}

/** POST /agencies/{agency_id}/spreadsheet-imports/{session_id}/commit */
export function commitImport(
  agencyId: string,
  sessionId: string,
): Promise<ImportCommitResult> {
  if (FF_MOCK_SPREADSHEET_API) return mockCommit(sessionId);
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/spreadsheet-imports/{session_id}/commit',
    path: { agencyId, session_id: sessionId },
  });
}

/**
 * POST /agencies/{agency_id}/spreadsheet-imports/{session_id}/commit/resume
 *
 * The "Retomar" button: a commit that failed or went quiet is enqueued again
 * from where it stopped. Answers what `commitImport` answers.
 */
export function resumeImportCommit(
  agencyId: string,
  sessionId: string,
): Promise<ImportCommitResult> {
  if (FF_MOCK_SPREADSHEET_API) return mockCommit(sessionId);
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/spreadsheet-imports/{session_id}/commit/resume',
    path: { agencyId, session_id: sessionId },
  });
}

/** POST /agencies/{agency_id}/spreadsheet-imports/{import_id}/undo */
export function undoImport(agencyId: string, importId: string): Promise<void> {
  if (FF_MOCK_SPREADSHEET_API) return mockUndo();
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/spreadsheet-imports/{import_id}/undo',
    path: { agencyId, import_id: importId },
  });
}

/**
 * POST /agencies/{agency_id}/spreadsheet-exports
 *
 * The mock never fabricates a file: a placeholder .xlsx would look like a
 * successful export of the agency's real list and it is not one. It answers
 * with the file name only, so the toast fires and nothing downloads.
 */
export async function createExport(
  agencyId: string,
  payload: {
    entity: SpreadsheetEntity;
    format: ExportFormat;
    filters: Record<string, unknown>;
    columns: string[];
  },
): Promise<ExportResult> {
  if (FF_MOCK_SPREADSHEET_API) {
    await new Promise<void>((resolve) => { setTimeout(resolve, 600); });
    const extension = payload.format === 'csv' ? 'csv' : 'xlsx';
    return { format: payload.format, file_name: `${payload.entity}.${extension}` };
  }
  const answer = await __request<{
    format: ExportFormat; file_name: string; content_base64: string;
    exported: number; total: number; truncated: boolean;
  }>(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/spreadsheet-exports',
    path: { agencyId },
    body: payload,
    mediaType: 'application/json',
  });
  return {
    format: answer.format,
    file_name: answer.file_name,
    blob: base64ToBlob(answer.content_base64, answer.format),
    exported: answer.exported,
    total: answer.total,
    truncated: answer.truncated,
  };
}

/** What a browser needs to save the bytes that arrived as text. */
const EXPORT_MEDIA_TYPES: Record<string, string> = {
  csv: 'text/csv;charset=utf-8',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/**
 * The base64 the endpoint answered, as a Blob to hand the browser.
 *
 * Decoded in chunks: `atob` returns a binary string and spreading a large
 * one into `String.fromCharCode` overflows the call stack, which is how an
 * export of a few thousand rows fails on the one machine that has them.
 */
function base64ToBlob(content: string, format: string): Blob {
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: EXPORT_MEDIA_TYPES[format] ?? 'application/octet-stream' });
}

/**
 * GET /agency/{agency_id}/spreadsheet-imports/history
 *
 * The Planilhas area asks for one thing now. It used to fold in connected
 * connected sheets and scheduled reports, neither of which was ever built
 * -- so with the mock off this threw before making a request, and the whole
 * area rendered as "Não conseguimos carregar esta área".
 */
export async function getHubData(agencyId: string): Promise<SpreadsheetHubData> {
  if (FF_MOCK_SPREADSHEET_API) return mockHub();
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/spreadsheet-imports/history',
    path: { agencyId },
  });
}

/** GET /agency/{agencyId}/spreadsheet-imports/history/{import_id} */
export async function getImportDetail(agencyId: string, importId: string): Promise<ImportDetail> {
  if (FF_MOCK_SPREADSHEET_API) return mockImportDetail(importId);
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/spreadsheet-imports/history/{import_id}',
    path: { agencyId, import_id: importId },
  });
}

/** GET /agencies/{agency_id}/spreadsheet-reports/{key}/download */
export async function downloadReport(agencyId: string, key: string): Promise<ExportResult> {
  if (!FF_MOCK_SPREADSHEET_API) {
    return notYet(`GET /agencies/${agencyId}/spreadsheet-reports/${key}/download`);
  }
  await new Promise<void>((resolve) => { setTimeout(resolve, 600); });
  return { format: 'xlsx', file_name: `${key}.xlsx` };
}
